import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import api from '../services/api';

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatDateOnly(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}

function formatBool(value) {
  return value ? 'Yes' : 'No';
}

function DetailItem({ label, value, fullWidth = false }) {
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.25, wordBreak: 'break-word' }}>
        {value ?? '—'}
      </Typography>
    </Grid>
  );
}

function ProfileSection({ title, children }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  );
}

function formatAddress(profile) {
  if (!profile) return '—';
  const parts = [profile.address, profile.city, profile.state, profile.zip_code].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

export default function UserDetailsDialog({
  open,
  userId,
  initialUser = null,
  onClose,
  onEdit,
}) {
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setUser(null);
      setError('');
      return;
    }

    if (!userId) return;

    setUser(initialUser);
    setLoading(true);
    setError('');

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/auth/admin/users/${userId}/`);
        if (!cancelled) {
          setUser(res.data);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.detail || e.response?.data?.error || 'Failed to load user details');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  const displayUser = user ?? initialUser;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>User details</DialogTitle>
      <DialogContent dividers>
        {loading && !displayUser && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {displayUser && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="h6" component="span">
                {displayUser.email}
              </Typography>
              {(displayUser.accessible_roles || []).map((role) => (
                <Chip key={role} label={role} size="small" variant="outlined" />
              ))}
            </Box>

            <Grid container spacing={2}>
              <DetailItem label="User ID" value={displayUser.id} fullWidth />
              <DetailItem label="Phone" value={displayUser.phone_number || '—'} />
              <DetailItem label="Primary role" value={displayUser.role} />
              <DetailItem label="Active" value={formatBool(displayUser.is_active)} />
              <DetailItem label="Staff" value={formatBool(displayUser.is_staff)} />
              <DetailItem label="Superuser" value={formatBool(displayUser.is_superuser)} />
              <DetailItem label="Created" value={formatDate(displayUser.created_at)} />
              <DetailItem label="Last updated" value={formatDate(displayUser.updated_at)} />
            </Grid>

            {displayUser.customer_profile && (
              <>
                <Divider sx={{ my: 2 }} />
                <ProfileSection title="Customer profile">
                  <DetailItem label="Company" value={displayUser.customer_profile.company_name} />
                  <DetailItem label="Phone" value={displayUser.customer_profile.phone_number} />
                  <DetailItem label="Team member" value={formatBool(displayUser.customer_profile.is_team_member)} />
                  {displayUser.customer_profile.is_team_member && (
                    <DetailItem
                      label="Primary company"
                      value={displayUser.customer_profile.parent_company_name}
                    />
                  )}
                  <DetailItem label="Address" value={formatAddress(displayUser.customer_profile)} fullWidth />
                  <DetailItem label="Profile ID" value={displayUser.customer_profile.id} fullWidth />
                  <DetailItem
                    label="Profile created"
                    value={formatDate(displayUser.customer_profile.created_at)}
                  />
                </ProfileSection>
              </>
            )}

            {displayUser.driver_profile && (
              <>
                <Divider sx={{ my: 2 }} />
                <ProfileSection title="Driver profile">
                  <DetailItem label="Name" value={displayUser.driver_profile.name} />
                  <DetailItem label="Phone" value={displayUser.driver_profile.phone_number} />
                  <DetailItem label="Status" value={displayUser.driver_profile.status} />
                  <DetailItem label="License number" value={displayUser.driver_profile.license_number} />
                  <DetailItem
                    label="License expiration"
                    value={formatDateOnly(displayUser.driver_profile.license_expiration_date)}
                  />
                  <DetailItem label="License state" value={displayUser.driver_profile.license_state} />
                  <DetailItem label="License class" value={displayUser.driver_profile.license_class} />
                  <DetailItem
                    label="License verified"
                    value={formatBool(displayUser.driver_profile.license_verified)}
                  />
                  <DetailItem label="Profile ID" value={displayUser.driver_profile.id} fullWidth />
                </ProfileSection>
              </>
            )}

            {displayUser.manager_profile && (
              <>
                <Divider sx={{ my: 2 }} />
                <ProfileSection title="Manager profile">
                  <DetailItem label="Name" value={displayUser.manager_profile.name} />
                  <DetailItem label="Email" value={displayUser.manager_profile.email} />
                  <DetailItem label="Phone" value={displayUser.manager_profile.phone_number || '—'} />
                  <DetailItem label="Profile ID" value={displayUser.manager_profile.id} fullWidth />
                  <DetailItem
                    label="Profile created"
                    value={formatDate(displayUser.manager_profile.created_at)}
                  />
                </ProfileSection>
              </>
            )}

            {!displayUser.customer_profile &&
              !displayUser.driver_profile &&
              !displayUser.manager_profile &&
              !loading && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No linked portal profiles yet.
                  </Typography>
                </Box>
              )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {onEdit && displayUser && (
          <Button
            startIcon={<EditIcon />}
            onClick={() => {
              onEdit(displayUser);
              onClose();
            }}
          >
            Edit
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
